import { useTranslation } from 'next-i18next';
import React, {
  FC, useCallback, useMemo,
} from 'react';
import type { DropdownOption } from 'src/common/components/DropdownDS22';
import type SearchStore from 'src/search/store/SearchStore';
import type { MiscParams } from 'src/search/types/SearchQuery/MiscParams';
import ActionsDropdown from 'src/common/components/ActionsDropdown';
import { trackEvent } from 'src/common/3rdParty/tracking';

/**
 * @description SortDropdown is a loose wrapper around ActionsDropdown,
 * and should only be used in Search Page.
 */
export type SortDropdownProps = {
  value: SearchStore['sort']['value'];
  onChange: SearchStore['sort']['onChange'];
};

/** Omitted sort param modeled in SearchStore as `null` but DropdownOption['id'] extends string */
type SortId = NonNullable<MiscParams['sort']> | 'default';

const GA4_EVENT_SORT = 'sort_in_search';
const gtagDetails: Record<SortId, string> = {
  default: 'Recommended',
  price: 'Price: Low to High',
  '-price': 'Price: High to Low',
  top_rated: 'Top Rated',
};

/**
 * Dropdown options map to search store "sort" param values
 * */
const SortDropdown: FC<SortDropdownProps> = ({ value, onChange }) => {
  const [t] = useTranslation('search-page');

  const options: DropdownOption<SortId>[] = useMemo(() => [
    {
      id: 'default', // not a valid search store "sort" value. handleOnChange will transform this to `null`
      dropdownLabel: t('sort-recommended'),
    },
    {
      id: 'price',
      dropdownLabel: t('sort-price-low'),
    },
    {
      id: '-price',
      dropdownLabel: t('sort-price-high'),
    },
    {
      id: 'top_rated',
      dropdownLabel: t('sort-top-rated'),
    },
  ], [t]);

  /** match search store sort value to option; else use default option */
  const selectedOption = value ? options.find((option) => option.id === value) : options[0];

  const handleOnChange = useCallback(
    (option: DropdownOption<SortId>) => {
      trackEvent(GA4_EVENT_SORT, {
        p1: gtagDetails[option.id],
      });
      onChange(
        option.id === 'default' ? null : option.id,
      );
    },
    [onChange],
  );

  return (
    <ActionsDropdown
      options={options}
      handleSelect={handleOnChange}
      defaultOption={selectedOption}
      mode="select"
      variant="compact"
      fullWidth
      dropdownPosition="right"
    />
  );
};

export default SortDropdown;
